import React, { useEffect, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { FaCamera } from "react-icons/fa";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Select from "react-select";
import {
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import hamburger from "../../assets/icons/black-hamburger.svg";

// Define interfaces for type safety
interface ProductImagesProp {
  media_url: string;
  sort_id: number;
  uuid?: string;
  isNew?: boolean;
}

interface DraggableImageProps {
  image: ProductImagesProp;
  index: number;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  deleteImage: (identifier: string, isNew: boolean) => void;
}

const DraggableImage: React.FC<DraggableImageProps> = ({
  image,
  index,
  moveImage,
  deleteImage,
}) => {
  const [, ref] = useDrag({
    type: "IMAGE",
    item: { index },
  });

  const [, drop] = useDrop({
    accept: "IMAGE",
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        moveImage(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const cloudinaryImageUrl = image.media_url.includes("cloudinary.com")
    ? image.media_url.replace(
        "/upload/",
        "/upload/w_791,h_624,c_fit,ar_9:16,f_auto,q_auto:best,c_lpad,b_auto:predominant,e_trim/"
      )
    : image.media_url;

  return (
    <Paper
      ref={(node) => ref(drop(node))}
      elevation={3}
      sx={{
        position: "relative",
        cursor: "move",
        height: "144px",
        width: "144px",
        overflow: "hidden",
        borderRadius: "8px",
        transition: "transform 0.2s",
        "&:hover": {
          transform: "scale(1.03)",
        },
      }}
    >
      <img
        src={cloudinaryImageUrl}
        alt={`Product Image ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <Button
        onClick={() =>
          deleteImage(
            image?.isNew ? index.toString() : image.uuid || "",
            !!image.isNew
          )
        }
        sx={{
          position: "absolute",
          top: "8px",
          right: "8px",
          minWidth: "36px",
          width: "36px",
          height: "36px",
          padding: 0,
          backgroundColor: "rgba(220, 38, 38, 0.8)",
          color: "white",
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: "rgba(185, 28, 28, 0.9)",
          },
        }}
      >
        <BsTrash size={18} />
      </Button>
    </Paper>
  );
};

export const AdminNewProduct = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [productImages, setProductImages] = useState<ProductImagesProp[]>([]);
  const [sizeKg, setSizeKg] = useState<string>("");
  const [sizePound, setSizePound] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState<
    { label: string; value: string }[]
  >([]);
  
  // Define the form data interface
  interface ProductFormValues {
    product_title: string;
    minimum_quantity: number;
    yourself_price: number;
    friends_price: number;
    category: string;
    size_kg: string;
    size_pounds: string;
    location: string;
    status: string;
    currency: string;
    product_description: string;
  }

  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
  } = useForm<ProductFormValues>({
    defaultValues: {
      status: "active",
      currency: "GHS",
    }
  });

  // For form watching
  const watchedStatus = watch("status");

  /**
   * Converts a single image file to a base64 string.
   */
  const convertImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64String = result.replace("data:", "").replace(/^.+,/, "");
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmitForm = async (values: any) => {
    setIsLoading(true);
    let payload = { ...values };

    // Convert string values to numbers
    payload.yourself_price = Number(values.yourself_price);
    payload.friends_price = Number(values.friends_price);
    payload.minimum_quantity = Number(values.minimum_quantity);

    // Process images
    const imagePromises = newImages.map(async (file, index) => {
      const bytes = await convertImageToBase64(file);
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      return {
        media_data: bytes,
        media_name: fileNameWithoutExtension,
        media_type: file.type,
        sort_id: productImages.length + index + 1,
      };
    });

    const newMediaData = await Promise.all(imagePromises);

    const finalImageData = productImages
      .filter((img) => !img.isNew)
      .map((img, index) => ({
        uuid: img.uuid,
        sort_id: index + 1,
      }));

    const combinedImages = [...finalImageData, ...newMediaData];

    payload.images = combinedImages.map((img, index) => ({
      ...img,
      sort_id: index + 1,
    }));

    try {
      if (uuid) {
        // When editing
        payload.product_uuid = uuid;

        if (selectedCategories.length > 0) {
          payload.category = selectedCategories[0]?.value;
        }

        const response = await api.put(
          `${environment.baseUrl}/admin/product/edit`,
          payload
        );

        if (response.data.success) {
          toast.success(response.data.message);
          navigate("/admin/products");
        } else {
          toast.error("Failed to update product");
        }
      } else {
        // When creating
        if (selectedCategories.length > 0) {
          payload.category = selectedCategories[0].value;
        } else {
          toast.error("Please select a category");
          setIsLoading(false);
          return;
        }

        const response = await api.post(
          `${environment.baseUrl}/admin/product/create`,
          payload
        );

        if (response.data.success) {
          toast.success(response.data.message);
          navigate("/admin/products");
        } else {
          toast.error("Failed to create product");
        }
      }
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    setProductImages((prevImages) => {
      const updatedImages = [...prevImages];
      const [movedImage] = updatedImages.splice(fromIndex, 1);
      updatedImages.splice(toIndex, 0, movedImage);
      return updatedImages.map((img, index) => ({
        ...img,
        sort_id: index + 1,
      }));
    });
  };

  const handleNewImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newImageFiles = Array.from(files);
      setNewImages((prevImages) => [...prevImages, ...newImageFiles]);

      setProductImages((prevImages) => {
        const lastSortId =
          prevImages.length > 0
            ? Math.max(...prevImages.map((img) => img.sort_id))
            : 0;

        const newProductImages = newImageFiles.map((file, index) => ({
          uuid: `new-${Date.now()}-${index}`,
          media_url: URL.createObjectURL(file),
          sort_id: lastSortId + index + 1,
          isNew: true,
        }));

        const updatedImages = [
          ...prevImages,
          ...newProductImages.filter(
            (newImage) =>
              !prevImages.some((prev) => prev.media_url === newImage.media_url)
          ),
        ];

        return updatedImages.map((image, index) => ({
          ...image,
          sort_id: index + 1,
        }));
      });
    }
  };

  const deleteImage = async (identifier: string, isNew: boolean) => {
    if (isNew) {
      // Handle deleting new images
      const indexToDelete = parseInt(identifier);
      setProductImages((prevImages) => {
        const updatedImages = prevImages.filter(
          (_, index) => index !== indexToDelete
        );
        return updatedImages.map((img, index) => ({
          ...img,
          sort_id: index + 1,
        }));
      });
      setNewImages((prevNewImages) =>
        prevNewImages.filter((_, index) => index !== indexToDelete)
      );
    } else {
      // Handle deleting existing images
      setProductImages((prevImages) => {
        const updatedImages = prevImages.filter(
          (img) => img.uuid !== identifier
        );
        return updatedImages.map((img, index) => ({
          ...img,
          sort_id: index + 1,
        }));
      });
      try {
        const response = await api.delete(
          `${environment.baseUrl}/admin/product/image/delete/${identifier}`
        );
        if (response.data.success) {
          toast.success(response.data.message);
        }
      } catch (error) {
        console.error("Error deleting image:", error);
        toast.error("Unable to delete image, try again.");
      }
    }
  };

  const fetchCategoriesList = async () => {
    try {
      const response = await api.get(`${environment.baseUrl}/admin/categories`);

      if (response.data.code == 200) {
        const categories = response.data.data.all_categories.map((x: any) => ({
          label: x.name,
          value: x.short_name,
        }));
        
        setCategoriesList(categories);
        
        if (uuid) {
          fetchProductDetails();
        }
      }
    } catch (error) {
      toast.error("Failed to fetch categories");
    }
  };

  const fetchProductDetails = async () => {
    try {
      const response = await api.get(
        `${environment.baseUrl}/product/fetch/single/${uuid}`
      );

      if (response.data.success) {
        const data_ = response.data.data.product;
        
        // Set form values
        setValue("location", data_.location);
        setValue("product_description", data_.product_description);
        setValue("size_kg", data_.size_kg);
        setValue("status", data_.status);
        setValue("currency", data_.currency);
        setValue("category", data_.category);
        setValue("size_pounds", data_.size_pounds);
        setValue("product_title", data_.product_title);
        setValue("minimum_quantity", data_.minimum_quantity);
        setValue("yourself_price", data_.yourself_price);
        setValue("friends_price", data_.friends_price);
        
        // Find and set the selected category
        const foundCategory = categoriesList.find(cat => cat.value === data_.category);
        
        if (foundCategory) {
          setSelectedCategories([foundCategory]);
        }

        // Load images
        if (response.data.data.product_images) {
          const apiImages = response.data.data.product_images;
          const sortedImages = apiImages
            .sort(
              (a: ProductImagesProp, b: ProductImagesProp) =>
                a.sort_id - b.sort_id
            )
            .map((img: ProductImagesProp) => ({ ...img, isNew: false }));

          setProductImages(sortedImages);
        }
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to load product details");
      navigate("/admin/products");
    }
  };

  useEffect(() => {
    fetchCategoriesList();
  }, []);

  return (
    <Box className="font-montserrat">
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <img src={hamburger} alt="products-icon" style={{ width: "18px" }} />
        <Typography variant="h5" fontWeight="bold">
          {uuid ? "Edit Product" : "New Product"}
        </Typography>
      </Box>

      <Paper
        elevation={2}
        sx={{
          padding: { xs: 3, md: 4 },
          borderRadius: "12px",
          backgroundColor: "#fff",
        }}
      >
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Box mb={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  mb={1.5}
                  sx={{ fontFamily: "Montserrat" }}
                >
                  PRODUCT TITLE
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter product title"
                  variant="outlined"
                  error={!!errors.product_title}
                  helperText={errors.product_title && "Product title is required"}
                  {...register("product_title", { required: true })}
                  sx={{ fontFamily: "Montserrat" }}
                />
              </Box>

              <Box mb={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  mb={1.5}
                  sx={{ fontFamily: "Montserrat" }}
                >
                  MINIMUM QUANTITY
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter minimum quantity"
                  type="number"
                  variant="outlined"
                  error={!!errors.minimum_quantity}
                  helperText={errors.minimum_quantity && "Minimum quantity is required"}
                  {...register("minimum_quantity", { required: true })}
                  sx={{ fontFamily: "Montserrat" }}
                />
              </Box>

              <Box mb={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  mb={1.5}
                  sx={{ fontFamily: "Montserrat" }}
                >
                  BUY YOURSELF PRICE
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter price"
                  type="number"
                  variant="outlined"
                  error={!!errors.yourself_price}
                  helperText={errors.yourself_price && "Price is required"}
                  {...register("yourself_price", { required: true })}
                  sx={{ fontFamily: "Montserrat" }}
                />
              </Box>

              <Box mb={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  mb={1.5}
                  sx={{ fontFamily: "Montserrat" }}
                >
                  BUY WITH FRIENDS PRICE
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter friends price"
                  type="number"
                  variant="outlined"
                  error={!!errors.friends_price}
                  helperText={errors.friends_price && "Friends price is required"}
                  {...register("friends_price", { required: true })}
                  sx={{ fontFamily: "Montserrat" }}
                />
              </Box>

              <Box mb={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  mb={1.5}
                  sx={{ fontFamily: "Montserrat" }}
                >
                  PRODUCT CATEGORY
                </Typography>
                <Select
                  options={categoriesList}
                  value={selectedCategories}
                  onChange={(selected: any) => setSelectedCategories(selected)}
                  placeholder="Select category"
                  isMulti={true}
                  styles={{
                    control: (base:any) => ({
                      ...base,
                      fontFamily: "Montserrat",
                      minHeight: '56px',
                      borderColor: errors.category ? '#d32f2f' : '#ced4da',
                      '&:hover': {
                        borderColor: errors.category ? '#d32f2f' : '#80bdff'
                      }
                    })
                  }}
                />
                {errors.category && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                    Category is required
                  </Typography>
                )}
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{fontFamily: "Montserrat" }}>
                  SIZE DETAILS
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="medium" mb={1} sx={{fontFamily: "Montserrat" }}>
                      Size in KG
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter size in KG"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography sx={{ ml: 1, fontFamily: "Montserrat"  }}>KGs</Typography>,
                      }}
                      error={!!errors.size_kg}
                      helperText={errors.size_kg && "Size in KG is required"}
                      {...register("size_kg", {
                        required: true,
                        onChange: (e) => setSizeKg(e.target.value),
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="medium" mb={1} 
                    sx={{fontFamily: "Montserrat" }}>
                      Size in Pounds
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter size in pounds"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography sx={{ ml: 1, fontFamily: "Montserrat"  }}>Pounds</Typography>,
                      }}
                      error={!!errors.size_pounds}
                      helperText={errors.size_pounds && "Size in pounds is required"}
                      {...register("size_pounds", {
                        required: true,
                        onChange: (e) => setSizePound(e.target.value),
                      })}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5} sx={{fontFamily: "Montserrat" }}>
                  LOCATION
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g. Houston, Texas"
                  variant="outlined"
                  error={!!errors.location}
                  helperText={errors.location && "Location is required"}
                  {...register("location", { required: true })}
                />
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5}
                sx={{fontFamily: "Montserrat" }}>
                  STATUS
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant={watchedStatus === "active" ? "contained" : "outlined"}
                      color={watchedStatus === "active" ? "primary" : "inherit"}
                      onClick={() => setValue("status", "active")}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        backgroundColor: watchedStatus === "active" ? "#4CAF50" : "transparent",
                        borderColor: watchedStatus === "active" ? "#4CAF50" : "#9e9e9e",
                        color: watchedStatus === "active" ? "#fff" : "#757575",
                        "&:hover": {
                          backgroundColor: watchedStatus === "active" ? "#388E3C" : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Active
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant={watchedStatus === "inactive" ? "contained" : "outlined"}
                      color={watchedStatus === "inactive" ? "error" : "inherit"}
                      onClick={() => setValue("status", "inactive")}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        backgroundColor: watchedStatus === "inactive" ? "#F44336" : "transparent",
                        borderColor: watchedStatus === "inactive" ? "#F44336" : "#9e9e9e",
                        color: watchedStatus === "inactive" ? "#fff" : "#757575",
                        "&:hover": {
                          backgroundColor: watchedStatus === "inactive" ? "#D32F2F" : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Inactive
                    </Button>
                  </Grid>
                </Grid>
                <input type="hidden" {...register("status", { required: true })} />
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5} sx={{fontFamily: "Montserrat" }}>
                  CURRENCY
                </Typography>
                <RadioGroup defaultValue="GHS" row>
                  <FormControlLabel
                    value="GHS"
                    control={<Radio {...register("currency")} />}
                    label="GHS"
                  />
                  <FormControlLabel
                    value="NGN"
                    control={<Radio {...register("currency")} />}
                    label="NGN"
                  />
                  <FormControlLabel
                    value="ZAR"
                    control={<Radio {...register("currency")} />}
                    label="ZAR"
                  />
                </RadioGroup>
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5} sx={{fontFamily: "Montserrat" }}>
                  PRODUCT IMAGES
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {/* Drag to reorder. Images should be at least 800x800px. */}
                </Typography>
                <DndProvider backend={HTML5Backend}>
                  <Grid container spacing={2} mb={2}>
                    {productImages.map((image, index) => (
                      <Grid item key={image.uuid}>
                        <DraggableImage
                          image={image}
                          index={index}
                          moveImage={moveImage}
                          deleteImage={deleteImage}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </DndProvider>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple={true}
                  onChange={handleNewImageUpload}
                  style={{ display: "none" }}
                />
                <label htmlFor="image-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<FaCamera />}
                    sx={{
                      borderColor: "#000",
                      color: "#000",
                      borderRadius: "8px",
                      py: 1,
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    Add Images
                  </Button>
                </label>
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5} sx={{fontFamily: "Montserrat" }}>
                  PRODUCT DESCRIPTION
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Enter product description"
                  variant="outlined"
                  error={!!errors.product_description}
                  helperText={errors.product_description && "Description is required"}
                  {...register("product_description", { required: true })}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box display="flex" justifyContent="center">
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                minWidth: "300px",
                py: 1.5,
                backgroundColor: "#0E203F",
                color: "#fff",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : uuid ? (
                "Update Product"
              ) : (
                "Create Product"
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};